import Reflux from "reflux";
import { SessionStore } from "shared/src/uidispatcherapi/SessionStore";
import CustomerStore from "./CustomerStore";
import CustomerHistoryService from "../services/CustomerHistoryService";
import { CustomerHistoryItem } from "../datatypes/CustomerHistory";
import CustomerHistoryAction from "../actions/CustomerHistoryAction";
import ArticleStore from "./ArticleStore";
import { parseArticleId } from "../utils/CustomerHistoryUtil";
import { ArticleRenderData } from "shared/src/types/RenderData";
import { ArticleStoreEntry, FetchedArticle } from "shared/src/stores/ArticleStore";

type State = {
    customerHistoryArticles: Map<string, CustomerHistoryItem>;
    renderData: Array<ArticleRenderData>;
    requestedIds: Array<string>;
    currentCustomerId: string | null | undefined;
    isLoadingArticles: boolean;
};
const state: State = {
    customerHistoryArticles: new Map(),
    renderData: [],
    requestedIds: [],
    currentCustomerId: null,
    isLoadingArticles: true
};
const MAX_ITEMS_SIZE = 6;
const MIN_ITEMS_SIZE = 3;

let currentCustomerIsFsdCustomer: boolean | null | undefined;

const CustomerHistoryArticlesStore = Reflux.createStore({
    init() {
        currentCustomerIsFsdCustomer = undefined;
        this.listenTo(SessionStore, this.onSessionStoreTrigger);
        this.listenTo(CustomerStore, this.onCustomerStoreTrigger);
        this.listenTo(ArticleStore, this.onArticleStoreTrigger);

        this.listenTo(CustomerHistoryAction.loadArticles, this.loadCustomerHistoryArticles);
        this.onSessionStoreTrigger();
    },

    onSessionStoreTrigger() {
        if (SessionStore.pending() || CustomerStore.isLoading()) {
            return;
        }
        this.loadCustomerHistoryArticles();
    },

    onCustomerStoreTrigger() {
        const newCustomerIsFsdCustomer = CustomerStore.isCurrentCustomerFsdCustomer();
        if (currentCustomerIsFsdCustomer !== newCustomerIsFsdCustomer) {
            this.loadCustomerHistoryArticles();
        }
    },

    onArticleStoreTrigger(articleMap: { [keys: string]: any }) {
        const idsWithArticleDataMissing = state.requestedIds.filter((id) => {
            const article = articleMap[id];
            return !article || ("state" in article && (article.state === "requested" || article.state === "loading"));
        });
        if (state.requestedIds.length > 0 && idsWithArticleDataMissing.length === 0) {
            this.setArticleRenderData(articleMap);
        }
    },

    setArticleRenderData(articleMap: { [keys: string]: any }) {
        const ids = [...state.customerHistoryArticles.keys()];
        if (!ids) {
            return;
        }
        const idsArticleDataExist = state.requestedIds.filter(
            (id) => articleMap[id] && (!("state" in articleMap[id]) || articleMap[id]?.state === "error")
        );

        const allRequestedArticlesHaveData = state.requestedIds.length === idsArticleDataExist.length;
        const enoughArticlesWithData = idsArticleDataExist.length >= MIN_ITEMS_SIZE;

        if (enoughArticlesWithData || allRequestedArticlesHaveData) {
            state.renderData = this.getValidArticlesToRender(articleMap);
            this.trigger();
        } else {
            const newIdsToFetch = ids.filter(
                (id) => !state.requestedIds.includes(id) && articleMap?.[id]?.state !== "error"
            );
            state.requestedIds = ids;
            if (newIdsToFetch?.length) {
                ArticleStore.fetchArticle(newIdsToFetch);
            }
        }
    },

    getValidArticlesToRender(articleMap: { [keys: string]: ArticleStoreEntry }): Array<FetchedArticle> {
        const result: Array<FetchedArticle> = [];
        Object.entries(articleMap).map(([id, item]) => {
            if (item && !("state" in item)) {
                (item as FetchedArticle).timestamp = this.getTimeStampForArticle(id);
                result.push(item as FetchedArticle);
            }
        });
        result.sort((item1, item2) => {
            return (item2.timestamp || 0) - (item1.timestamp || 0);
        });
        return result.slice(0, MAX_ITEMS_SIZE);
    },

    loadCustomerHistoryArticles() {
        const customerId: string | undefined | null = SessionStore.getCustomerId();
        const customerIdChanged = customerId !== state.currentCustomerId;

        if (customerId && customerIdChanged) {
            state.customerHistoryArticles = new Map();
            state.isLoadingArticles = true;
            const country = SessionStore.getCountry();
            this.fetchCustomerHistoryArticlesForCustomer(country, customerId);
            this.trigger();
        }
        if (customerId) {
            state.currentCustomerId = customerId;
        }
    },

    getTimeStampForArticle(id: string): number {
        const customerHistoryItem = state.customerHistoryArticles.get(id);
        return customerHistoryItem && customerHistoryItem.timestamp ? Date.parse(customerHistoryItem.timestamp) : 0;
    },

    fetchCustomerHistoryArticlesForCustomer(country: string, customerId: string) {
        const self = this;
        const templateTypes = ["productpage"];
        CustomerHistoryService.getAllCustomerHistoryByTemplateTypes(country, customerId, templateTypes)
            .then(self.setCustomerHistoryArticles)
            .catch(self.getCustomerHistoryArticlesFailed);
    },

    setCustomerHistoryArticles(response: any) {
        state.isLoadingArticles = false;
        const filteredResponse = response.filter((item: any) => "sourcePath" in item && item.sourcePath !== null);

        filteredResponse.map((item: any) => {
            const articleId = parseArticleId(item.sourcePath);
            if (articleId && !(articleId in state.customerHistoryArticles)) {
                // filter duplicate, keep only first entry with most recent timestamp
                item.name = item.pageHeadline;
                state.customerHistoryArticles.set(articleId, item);
            }
        });

        const ids = [...state.customerHistoryArticles.keys()];
        if (ids) {
            state.requestedIds = ids;
            ArticleStore.fetchArticle(ids);
        }
        this.trigger();
    },

    getCustomerHistoryData(): State {
        return state;
    },

    getCustomerHistoryArticleRenderData(): ArticleRenderData[] {
        return state.renderData;
    },

    getCustomerHistoryArticlesFailed() {
        state.isLoadingArticles = false;
        state.customerHistoryArticles = new Map();
        this.trigger();
    },

    // For testing only
    resetCustomerId() {
        state.currentCustomerId = null;
    },

    resetRequestedIds() {
        state.requestedIds = [];
    }
});

export default CustomerHistoryArticlesStore;
